import { useState } from 'react'

export enum ComponentState {
  Loading,
  Succeed,
  Failed
}

export function useComponentStateMachine() {
  const [current, setState] = useState<ComponentState>(ComponentState.Loading)

  function canTransition(from: ComponentState, to: ComponentState) {
    switch (from) {
      case ComponentState.Loading:
        return to === ComponentState.Succeed || to === ComponentState.Failed
      case ComponentState.Succeed:
        // Succeed -> Failed is a real transition with placeholderData (and
        // keepPreviousData): the query reports "success" on the very first
        // render (placeholder shown, so isLoading never goes true) and a
        // failing real fetch then flips straight to "error". It also covers
        // a failing background refetch. Without this the render-time
        // dataTransition throws "illegal transition" and the whole route
        // crashes into the router errorElement instead of the ErrorView.
        return to === ComponentState.Loading || to === ComponentState.Failed
      case ComponentState.Failed:
        // Failed -> Succeed: after an error, switching to an already-cached
        // (or placeholder-backed) query key yields "success" immediately
        // (isLoading stays false).
        return to === ComponentState.Loading || to === ComponentState.Succeed
    }
  }

  function transitionTo(to: ComponentState) {
    if (to === current) {
      return
    }

    if (!canTransition(current, to)) {
      throw new Error('illegal transition')
    }

    setState(to)
  }

  function transitionOrStaySucceeded() {
    transitionTo(ComponentState.Succeed)
  }

  function transitionOrStayFailed() {
    transitionTo(ComponentState.Failed)
  }

  function transitionOrStayLoading() {
    transitionTo(ComponentState.Loading)
  }

  function dataTransition(isLoading: boolean, isSucceeded: boolean, isFailed: boolean) {
    if (isSucceeded) {
      transitionOrStaySucceeded()
    } else if (isFailed) {
      transitionOrStayFailed()
    } else if (isLoading) {
      transitionOrStayLoading()
    }
  }

  return {
    state: current,
    transition: transitionTo,
    dataTransition: dataTransition
  }
}